import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  CheckboxCard,
  CollapsibleSection,
  ColorSwatchField,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  PreHeader,
  RadioCardGroup,
  SegmentedSetting,
  Select,
  Stack,
  Text,
  Textarea,
  ToggleRow,
  VStack,
} from "components/ui";
import { TranslationTabs } from "components/common/TranslationTabs";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import { MdSave } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { isCustomTriggerDisplay } from "services/widgetService";
import { UrlVisibilityEditor } from "views/admin/widgets/components/UrlVisibilityEditor";
import WidgetSelector from "views/admin/widgets/components/WidgetSelector";
import { useWidgetEditor } from "views/admin/widgets/hooks/useWidgetEditor";
import { ChatLabelsSection } from "./ChatLabelsSection";
import { ShortcutCardEditor } from "./ShortcutCardEditor";

// Two parallel arrays driving the light/dark color swatch grids. Keys match
// `styleOverrides` exactly so updates flow through the same `updateStyleOverride`
// path the old `Input type="color"` rows used. Fallbacks mirror the design
// kit's reference palette so unfilled cells still render a sensible swatch.
const LIGHT_COLOR_FIELDS = [
  { key: "primaryColorLight", label: "Primary colour", fallback: "#3B82F6" },
  {
    key: "sendButtonHoverLight",
    label: "Send button hover",
    fallback: "#1d4ed8",
  },
  {
    key: "secondaryColorLight",
    label: "Secondary colour",
    fallback: "#F5F5F5",
  },
  { key: "pageBgLight", label: "Page background", fallback: "#FFFFFF" },
  { key: "pageTextLight", label: "Page text", fallback: "#1D1D1F" },
  { key: "sidebarBgLight", label: "Sidebar background", fallback: "#FFFFFF" },
  { key: "sidebarTextLight", label: "Sidebar text", fallback: "#1D1D1F" },
  { key: "inputBgLight", label: "Input background", fallback: "#E3E3E7" },
  { key: "inputTextLight", label: "Input text", fallback: "#141417" },
  {
    key: "messageUserBgLight",
    label: "User message background",
    fallback: "#3B82F6",
  },
  {
    key: "messageUserTextLight",
    label: "User message text",
    fallback: "#FFFFFF",
  },
  {
    key: "messageAssistantTextLight",
    label: "Assistant message text",
    fallback: "#1D1D1F",
  },
  { key: "borderColorLight", label: "Border colour", fallback: "#E3E3E7" },
] as const;

const DARK_COLOR_FIELDS = [
  { key: "primaryColorDark", label: "Primary colour", fallback: "#3B82F6" },
  {
    key: "sendButtonHoverDark",
    label: "Send button hover",
    fallback: "#1d4ed8",
  },
  { key: "secondaryColorDark", label: "Secondary colour", fallback: "#141417" },
  { key: "pageBgDark", label: "Page background", fallback: "#141417" },
  { key: "pageTextDark", label: "Page text", fallback: "#FFFFFF" },
  { key: "sidebarBgDark", label: "Sidebar background", fallback: "#141417" },
  { key: "sidebarTextDark", label: "Sidebar text", fallback: "#FFFFFF" },
  { key: "inputBgDark", label: "Input background", fallback: "#3B3B3C" },
  { key: "inputTextDark", label: "Input text", fallback: "#FFFFFF" },
  {
    key: "messageUserBgDark",
    label: "User message background",
    fallback: "#3B82F6",
  },
  {
    key: "messageUserTextDark",
    label: "User message text",
    fallback: "#FFFFFF",
  },
  {
    key: "messageAssistantTextDark",
    label: "Assistant message text",
    fallback: "#FFFFFF",
  },
  { key: "borderColorDark", label: "Border colour", fallback: "#515153" },
] as const;

const UxPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | undefined>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return params.get("widgetId") ?? undefined;
    },
  );

  const handleSelect = (id: string) => {
    setSelectedWidgetId(id || undefined);
    const params = new URLSearchParams(window.location.search);
    if (id) {
      params.set("widgetId", id);
    } else {
      params.delete("widgetId");
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  const {
    widget,
    loading,
    saving,
    error,
    success,
    formData,
    setFormData,
    styleOverridesEnabled,
    setStyleOverridesEnabled,
    styleOverrides,
    updateStyleOverride,
    requiresEndUserAuth,
    setRequiresEndUserAuth,
    portalEnabled,
    setPortalEnabled,
    portalMembersEnabled,
    setPortalMembersEnabled,
    portalKnowledgeBaseEnabled,
    setPortalKnowledgeBaseEnabled,
    portalSkillsEnabled,
    setPortalSkillsEnabled,
    portalWorkflowsEnabled,
    setPortalWorkflowsEnabled,
    portalIntegrationsEnabled,
    setPortalIntegrationsEnabled,
    portalUsageEnabled,
    setPortalUsageEnabled,
    portalSettingsEnabled,
    setPortalSettingsEnabled,
    portalPromptsEnabled,
    setPortalPromptsEnabled,
    promptShortcutsEnabled,
    setPromptShortcutsEnabled,
    shortcutCards,
    setShortcutCards,
    welcomeScreenText,
    setWelcomeScreenText,
    welcomeScreenTextTranslations,
    setWelcomeScreenTextTranslations,
    chatLabels,
    setChatLabels,
    handleSave,
  } = useWidgetEditor(selectedWidgetId);

  const [displayOpen, setDisplayOpen] = useState(true);
  const [portalOpen, setPortalOpen] = useState(true);
  const [stylesOpen, setStylesOpen] = useState(true);
  const [shortcutsOpen, setShortcutsOpen] = useState(true);
  const [selectorInView, setSelectorInView] = useState(true);
  const [bannerLeft, setBannerLeft] = useState(0);
  const selectorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayOpen(true);
    setStylesOpen(true);
    setShortcutsOpen(true);
  }, [selectedWidgetId]);

  useEffect(() => {
    const check = () => {
      const el = selectorRef.current;
      if (!el) return;
      setSelectorInView(el.getBoundingClientRect().bottom > 0);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  useEffect(() => {
    const update = () => {
      if (contentRef.current) {
        setBannerLeft(contentRef.current.getBoundingClientRect().left);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <Stack gap={5} w="full" ref={contentRef}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={2}>
        <VStack align="start" gap={1}>
          <Heading size="lg" color={{ base: "navy.700", _dark: "white" }}>
            User Interface
          </Heading>
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            Configure the look, feel, and placement of each widget.
          </Text>
        </VStack>
        <AskStrikeUpButton context={t("app.askStrikeUp.context.ux")} />
      </Flex>

      {/* Widget selector */}
      <Box ref={selectorRef}>
        <WidgetSelector
          selectedWidgetId={selectedWidgetId}
          onSelect={handleSelect}
        />
      </Box>

      {/* Fixed context banner — shown only when widget selector is scrolled out of view */}
      {selectedWidgetId && !loading && formData.name && !selectorInView && (
        <HStack
          position="fixed"
          top={0}
          left={`${bannerLeft}px`}
          right={0}
          zIndex={49}
          px={4}
          py={2}
          bg={{ base: "blue.50", _dark: "blue.950" }}
          borderBottomWidth="1px"
          borderColor={{ base: "blue.200", _dark: "blue.700" }}
          gap={2}
          boxShadow="sm"
        >
          <Text fontSize="xs" color={{ base: "blue.600", _dark: "blue.300" }}>
            Editing settings for
          </Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={{ base: "blue.700", _dark: "blue.200" }}
          >
            {formData.name}
          </Text>
        </HStack>
      )}

      {!selectedWidgetId ? (
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.500", _dark: "gray.400" }}
              py={6}
            >
              Select a widget above to configure its User Interface settings.
            </Text>
          </Card.Body>
        </Card.Root>
      ) : loading ? (
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              Loading…
            </Text>
          </Card.Body>
        </Card.Root>
      ) : (
        <>
          {/* Data display */}
          <CollapsibleSection
            pre="Display"
            title="Data display"
            description="Widget name, dimensions, display type and button appearance"
            isOpen={displayOpen}
            onOpenChange={setDisplayOpen}
            rootProps={{ overflow: "visible" }}
            bodyProps={{ overflow: "visible" }}
            contentRootProps={{ style: { overflow: "visible" } }}
            contentProps={{ overflow: "visible" }}
          >
            <Grid
              templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
              gap={5}
            >
              {/* Left column */}
              <Stack gap={4}>
                <Input
                  label="Widget name"
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="My widget"
                />

                <Box>
                  <Text
                    fontSize="sm"
                    color="var(--text-secondary-color)"
                    mb={2}
                  >
                    Display type
                  </Text>
                  <RadioCardGroup
                    value={formData.displayType}
                    onChange={(id) =>
                      setFormData({ ...formData, displayType: id })
                    }
                    options={[
                      {
                        id: "popup",
                        label: t("app.widgets.ux.displayTypes.popup"),
                      },
                      {
                        id: "sidebar",
                        label: t("app.widgets.ux.displayTypes.sidebar"),
                      },
                      {
                        id: "panel",
                        label: t("app.widgets.ux.displayTypes.panel"),
                      },
                      {
                        id: "custom-trigger",
                        label: t(
                          "app.widgets.ux.displayTypes.customTriggerSidebar",
                        ),
                        sub: t(
                          "app.widgets.ux.displayTypes.customTriggerSidebarHint",
                        ),
                      },
                      {
                        id: "custom-trigger-panel",
                        label: t(
                          "app.widgets.ux.displayTypes.customTriggerPanel",
                        ),
                        sub: t(
                          "app.widgets.ux.displayTypes.customTriggerPanelHint",
                        ),
                      },
                    ]}
                  />
                </Box>

                {(formData.displayType === "sidebar" ||
                  isCustomTriggerDisplay(formData.displayType)) && (
                  <Box>
                    <Text
                      fontSize="sm"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      Display behaviour
                    </Text>
                    <Text
                      fontSize="xs"
                      color="var(--text-secondary-color)"
                      mb={2}
                    >
                      Overlay floats above the page; Push compresses host
                      content so nothing is covered.
                    </Text>
                    <RadioCardGroup
                      value={formData.displayBehaviour || "overlay"}
                      onChange={(id) =>
                        setFormData({ ...formData, displayBehaviour: id })
                      }
                      options={[
                        {
                          id: "overlay",
                          label: "Overlay",
                          sub: "slides over the page",
                        },
                        {
                          id: "push",
                          label: "Push",
                          sub: "shifts page content to the left",
                        },
                      ]}
                    />
                  </Box>
                )}

                <ToggleRow
                  label="Enable feedback"
                  hint="Allow users to rate AI responses with thumbs up/down."
                  value={!!formData.enableFeedback}
                  onChange={(v) =>
                    setFormData({ ...formData, enableFeedback: v })
                  }
                />
              </Stack>

              {/* Middle column */}
              <Stack gap={4}>
                <Textarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Widget description"
                  rows={1}
                  height="40px"
                  minH="40px"
                  resize="vertical"
                />

                <Input
                  label="Width"
                  type="text"
                  value={formData.iframeWidth}
                  onChange={(e) =>
                    setFormData({ ...formData, iframeWidth: e.target.value })
                  }
                  placeholder="400px"
                  helperText="CSS size value (e.g. 400px, 50%)"
                />

                {formData.displayType !== "sidebar" &&
                  formData.displayType !== "panel" &&
                  !isCustomTriggerDisplay(formData.displayType) && (
                    <Input
                      label="Height"
                      type="text"
                      value={formData.iframeHeight}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          iframeHeight: e.target.value,
                        })
                      }
                      placeholder="600px"
                      helperText="CSS size value"
                    />
                  )}

                {/* Panel mode renders no toggle button, so it has no button colors. */}
                {/* Custom-trigger DOES render a minimize tab on the right edge — keep colors editable. */}
                {formData.displayType !== "panel" && (
                  <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                    <ColorSwatchField
                      label="Button color"
                      value={formData.buttonColor || "#FFFFFF"}
                      onChange={(v) =>
                        setFormData({ ...formData, buttonColor: v })
                      }
                    />
                    <ColorSwatchField
                      label="Button background"
                      value={formData.buttonBackgroundColor || "#3B82F6"}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          buttonBackgroundColor: v,
                        })
                      }
                    />
                  </Grid>
                )}

                {formData.displayType !== "sidebar" &&
                  formData.displayType !== "panel" &&
                  !isCustomTriggerDisplay(formData.displayType) && (
                    <>
                      <Input
                        label="Button size"
                        type="text"
                        value={formData.buttonSize}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            buttonSize: e.target.value,
                          })
                        }
                        placeholder="3.5rem"
                        helperText="CSS size value"
                      />
                      <Input
                        label="Button margin"
                        type="text"
                        value={formData.buttonMargin}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            buttonMargin: e.target.value,
                          })
                        }
                        placeholder="1rem"
                        helperText="CSS margin value"
                      />
                    </>
                  )}
              </Stack>
            </Grid>
          </CollapsibleSection>

          {/* Colours and styling */}
          <CollapsibleSection
            pre="Theme"
            title="Colours and styling"
            description="Override default theme colours, chart colours and border styles"
            isOpen={stylesOpen}
            onOpenChange={setStylesOpen}
          >
            <Stack gap={4}>
              <ToggleRow
                label="Override default theme colours"
                value={styleOverridesEnabled}
                onChange={setStyleOverridesEnabled}
              />

              {styleOverridesEnabled && (
                <Stack gap={5} mt={2}>
                  <Box>
                    <Box mb={2}>
                      <PreHeader size="xs">Light theme</PreHeader>
                    </Box>
                    <Grid
                      templateColumns={{
                        base: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      }}
                      gap={3}
                    >
                      {LIGHT_COLOR_FIELDS.map((f) => (
                        <ColorSwatchField
                          key={f.key}
                          label={f.label}
                          value={
                            (styleOverrides[
                              f.key as keyof typeof styleOverrides
                            ] as string) || f.fallback
                          }
                          onChange={(v) => updateStyleOverride(f.key as any, v)}
                        />
                      ))}
                    </Grid>
                  </Box>

                  <Box>
                    <Box mb={2}>
                      <PreHeader size="xs">Dark theme</PreHeader>
                    </Box>
                    <Grid
                      templateColumns={{
                        base: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      }}
                      gap={3}
                    >
                      {DARK_COLOR_FIELDS.map((f) => (
                        <ColorSwatchField
                          key={f.key}
                          label={f.label}
                          value={
                            (styleOverrides[
                              f.key as keyof typeof styleOverrides
                            ] as string) || f.fallback
                          }
                          onChange={(v) => updateStyleOverride(f.key as any, v)}
                        />
                      ))}
                    </Grid>
                  </Box>

                  <Box>
                    <Box mb={2}>
                      <PreHeader size="xs">Chart colours</PreHeader>
                    </Box>
                    <Grid
                      templateColumns={{
                        base: "repeat(2, 1fr)",
                        sm: "repeat(3, 1fr)",
                        md: "repeat(5, 1fr)",
                      }}
                      gap={3}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <ColorSwatchField
                          key={n}
                          label={`Colour ${n}`}
                          value={
                            (styleOverrides[
                              `chartColor${n}` as keyof typeof styleOverrides
                            ] as string) || "#888888"
                          }
                          onChange={(v) =>
                            updateStyleOverride(`chartColor${n}` as any, v)
                          }
                        />
                      ))}
                    </Grid>
                  </Box>

                  <Box>
                    <Box mb={2}>
                      <PreHeader size="xs">Border</PreHeader>
                    </Box>
                    <Grid
                      templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                      gap={4}
                    >
                      <Input
                        label="Border radius"
                        type="text"
                        value={styleOverrides.borderRadius}
                        onChange={(e) =>
                          updateStyleOverride("borderRadius", e.target.value)
                        }
                        placeholder="12px"
                        helperText="CSS value"
                      />
                      <Input
                        label="Border width"
                        type="text"
                        value={styleOverrides.borderWidth}
                        onChange={(e) =>
                          updateStyleOverride("borderWidth", e.target.value)
                        }
                        placeholder="0px"
                        helperText="CSS value"
                      />
                      <Select
                        label="Border style"
                        items={[
                          { label: "Solid", value: "solid" },
                          { label: "Dashed", value: "dashed" },
                          { label: "Dotted", value: "dotted" },
                        ]}
                        value={[styleOverrides.borderStyle]}
                        onValueChange={(e: any) =>
                          updateStyleOverride("borderStyle", e.value[0])
                        }
                      />
                      <Input
                        label={t("app.widgets.ux.iframeCornerRadius.label", {
                          defaultValue: "Widget corner radius (outer)",
                        })}
                        type="text"
                        value={styleOverrides.iframeCornerRadius}
                        onChange={(e) =>
                          updateStyleOverride(
                            "iframeCornerRadius",
                            e.target.value,
                          )
                        }
                        placeholder="12px"
                        helperText={t(
                          "app.widgets.ux.iframeCornerRadius.help",
                          {
                            defaultValue:
                              "Rounds the widget's outer corners on the host page. Leave blank to keep the default.",
                          },
                        )}
                      />
                    </Grid>
                  </Box>
                </Stack>
              )}

              {/* Typography — custom fonts. Independent of the colour-override
                  toggle above, so a tenant can rebrand the font without touching
                  the theme colours. */}
              <Box>
                <Box mb={2}>
                  <PreHeader size="xs">
                    {t("app.widgets.ux.typography.title", {
                      defaultValue: "Typography",
                    })}
                  </PreHeader>
                </Box>
                <Text fontSize="xs" color="var(--text-secondary-color)" mb={3}>
                  {t("app.widgets.ux.typography.description", {
                    defaultValue:
                      "Use your own fonts in the widget. Provide a Google Fonts link or a direct .woff2/.ttf URL. You are responsible for the font's licence and for serving it with CORS enabled.",
                  })}
                </Text>
                <Stack gap={4}>
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={4}
                  >
                    <Input
                      label={t("app.widgets.ux.typography.bodyFamily", {
                        defaultValue: "Body font name",
                      })}
                      type="text"
                      value={formData.customFonts.bodyFamily}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customFonts: {
                            ...formData.customFonts,
                            bodyFamily: e.target.value,
                          },
                        })
                      }
                      placeholder="Inter"
                      helperText={t("app.widgets.ux.typography.familyHelp", {
                        defaultValue: "Must match the font's family name",
                      })}
                    />
                    <Input
                      label={t("app.widgets.ux.typography.bodyUrl", {
                        defaultValue: "Body font URL",
                      })}
                      type="text"
                      value={formData.customFonts.bodyUrl}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customFonts: {
                            ...formData.customFonts,
                            bodyUrl: e.target.value,
                          },
                        })
                      }
                      placeholder="https://fonts.googleapis.com/css2?family=Inter&display=swap"
                      helperText={t("app.widgets.ux.typography.urlHelp", {
                        defaultValue: "Google Fonts link or a .woff2/.ttf URL",
                      })}
                    />
                  </Grid>
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={4}
                  >
                    <Input
                      label={t("app.widgets.ux.typography.headingFamily", {
                        defaultValue: "Heading font name (optional)",
                      })}
                      type="text"
                      value={formData.customFonts.headingFamily}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customFonts: {
                            ...formData.customFonts,
                            headingFamily: e.target.value,
                          },
                        })
                      }
                      placeholder=""
                      helperText={t("app.widgets.ux.typography.headingHelp", {
                        defaultValue: "Leave blank to use the body font",
                      })}
                    />
                    <Input
                      label={t("app.widgets.ux.typography.headingUrl", {
                        defaultValue: "Heading font URL (optional)",
                      })}
                      type="text"
                      value={formData.customFonts.headingUrl}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          customFonts: {
                            ...formData.customFonts,
                            headingUrl: e.target.value,
                          },
                        })
                      }
                      placeholder=""
                      helperText={t("app.widgets.ux.typography.urlHelp", {
                        defaultValue: "Google Fonts link or a .woff2/.ttf URL",
                      })}
                    />
                  </Grid>
                </Stack>
              </Box>
            </Stack>
          </CollapsibleSection>

          {/* Customer Portal */}
          <CollapsibleSection
            pre="Portal"
            title={t("app.widgets.ux.customerPortal.sectionTitle", {
              defaultValue: "Customer Portal",
            })}
            description={t("app.widgets.ux.customerPortal.sectionDescription", {
              defaultValue:
                "Control whether the widget exposes the Customer Portal and which pages your customers can see.",
            })}
            isOpen={portalOpen}
            onOpenChange={setPortalOpen}
          >
            <Stack gap={4}>
              <SegmentedSetting
                label={t("app.widgets.ux.requireCustomerAuth", {
                  defaultValue: "Require customer authentication",
                })}
                hint={t("app.widgets.ux.requireCustomerAuthHelp", {
                  defaultValue:
                    "When on, customers must set a PIN or password the first time they open this widget, and enter it on subsequent visits. Existing customers get a one-time setup popup on next visit; their chats and authorizations are preserved.",
                })}
                value={requiresEndUserAuth}
                onChange={setRequiresEndUserAuth}
              />

              <SegmentedSetting
                label={t("app.widgets.ux.customerPortal.masterToggle", {
                  defaultValue: "Enable Customer Portal",
                })}
                hint={t("app.widgets.ux.customerPortal.masterHelp", {
                  defaultValue:
                    "Hides the Configure button inside chat. Customers who already have the direct-link Portal URL can still open it — this only controls the in-chat entry point.",
                })}
                value={portalEnabled}
                onChange={setPortalEnabled}
              />

              <Input
                label={t("app.widgets.ux.customerPortal.appPageUrl", {
                  defaultValue: "App page URL",
                })}
                type="url"
                value={formData.appPageUrl}
                onChange={(e) =>
                  setFormData({ ...formData, appPageUrl: e.target.value })
                }
                placeholder="https://app.example.com/account"
                helperText={t("app.widgets.ux.customerPortal.appPageUrlHelp", {
                  defaultValue:
                    "The https page of your app that embeds this widget. Email, Slack and Telegram notifications link here, and the widget opens the chat or run they are about. Runs open only while the Customer Portal is on.",
                })}
              />

              {/* Visible pages — checkbox-chip grid */}
              <Box
                p={3.5}
                bg="var(--bg-secondary)"
                borderWidth="1px"
                borderColor="var(--border-default)"
                borderRadius="lg"
                opacity={portalEnabled ? 1 : 0.55}
                pointerEvents={portalEnabled ? "auto" : "none"}
                transition="opacity 0.15s"
              >
                <Text
                  fontSize="sm"
                  fontWeight={500}
                  color="var(--text-primary)"
                  m={0}
                >
                  {t("app.widgets.ux.customerPortal.pagesLabel", {
                    defaultValue: "Visible pages",
                  })}
                </Text>
                <Text
                  fontSize="xs"
                  color="var(--text-secondary-color)"
                  mt={1}
                  mb={3.5}
                  lineHeight="1.5"
                  maxW="720px"
                >
                  {t("app.widgets.ux.customerPortal.pagesHelp", {
                    defaultValue:
                      "Turn individual Portal pages on or off. Disabled pages disappear from the sidebar everywhere the Portal renders (in-chat AND via direct link). Profile is always visible.",
                  })}
                </Text>
                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  gap={2.5}
                >
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.profile", {
                      defaultValue: "Profile",
                    })}
                    checked
                    locked
                    alwaysOnLabel={t("app.widgets.ux.customerPortal.alwaysOn", {
                      defaultValue: "Always on",
                    })}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.members", {
                      defaultValue: "Members",
                    })}
                    checked={portalMembersEnabled}
                    onChange={setPortalMembersEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t(
                      "app.widgets.ux.customerPortal.pages.knowledgeBase",
                      { defaultValue: "Knowledge Base" },
                    )}
                    checked={portalKnowledgeBaseEnabled}
                    onChange={setPortalKnowledgeBaseEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.skills", {
                      defaultValue: "Skills",
                    })}
                    checked={portalSkillsEnabled}
                    onChange={setPortalSkillsEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.workflows", {
                      defaultValue: "Workflows",
                    })}
                    checked={portalWorkflowsEnabled}
                    onChange={setPortalWorkflowsEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t(
                      "app.widgets.ux.customerPortal.pages.integrations",
                      { defaultValue: "Notification channels" },
                    )}
                    checked={portalIntegrationsEnabled}
                    onChange={setPortalIntegrationsEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.usage", {
                      defaultValue: "Usage",
                    })}
                    checked={portalUsageEnabled}
                    onChange={setPortalUsageEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.settings", {
                      defaultValue: "Settings",
                    })}
                    checked={portalSettingsEnabled}
                    onChange={setPortalSettingsEnabled}
                    disabled={!portalEnabled}
                  />
                  <CheckboxCard
                    label={t("app.widgets.ux.customerPortal.pages.prompts", {
                      defaultValue: "Prompt Shortcuts",
                    })}
                    checked={portalPromptsEnabled}
                    onChange={setPortalPromptsEnabled}
                    disabled={!portalEnabled}
                  />
                </Grid>
              </Box>
            </Stack>
          </CollapsibleSection>

          {/* Co-pilot Shortcut Cards */}
          <CollapsibleSection
            pre="Co-pilot"
            title={t("app.widgets.ux.shortcuts.sectionTitle", {
              defaultValue: "Shortcut cards",
            })}
            description={t("app.widgets.ux.shortcuts.sectionDescription", {
              defaultValue:
                "Quick-action buttons shown on the chat welcome screen. Clicking a card sends its prompt instantly.",
            })}
            isOpen={shortcutsOpen}
            onOpenChange={setShortcutsOpen}
          >
            <Stack gap={4}>
              <TranslationTabs
                fields={[
                  {
                    key: "text",
                    label: t("app.widgets.ux.shortcuts.welcomeText.label", {
                      defaultValue: "Welcome screen text",
                    }),
                    type: "textarea",
                    rows: 2,
                    maxLength: 500,
                    placeholder: t(
                      "app.widgets.ux.shortcuts.welcomeText.placeholder",
                      {
                        defaultValue:
                          "e.g. Hi there! How can I help you today?",
                      },
                    ),
                    helperText: t(
                      "app.widgets.ux.shortcuts.welcomeText.helperText",
                      {
                        defaultValue:
                          "Shown above the shortcut cards on the chat welcome screen. Leave empty to hide.",
                      },
                    ),
                  },
                ]}
                defaultValues={{ text: welcomeScreenText }}
                onDefaultChange={(values) =>
                  setWelcomeScreenText(values.text ?? "")
                }
                translations={welcomeScreenTextTranslations}
                onTranslationsChange={setWelcomeScreenTextTranslations}
              />
              <ShortcutCardEditor
                cards={shortcutCards}
                onChange={setShortcutCards}
              />
            </Stack>
          </CollapsibleSection>

          <ChatLabelsSection
            chatLabels={chatLabels}
            onChatLabelsChange={setChatLabels}
          />

          {/* Placement (URL visibility) — UrlVisibilityEditor manages its own collapsible */}
          <UrlVisibilityEditor
            includeUrls={formData.includeUrls}
            excludeUrls={formData.excludeUrls}
            onChange={(includeUrls, excludeUrls) =>
              setFormData({ ...formData, includeUrls, excludeUrls })
            }
          />

          {/* Save */}
          <Box
            pt={3}
            borderTopWidth="1px"
            borderColor={{ base: "gray.200", _dark: "whiteAlpha.100" }}
          >
            <Button
              onClick={handleSave}
              intent="primary"
              disabled={saving}
              alignSelf="flex-start"
            >
              <MdSave />
              {saving ? "Saving…" : "Save User Interface Settings"}
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default UxPage;
